import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, Search } from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import { Badge, Button, Card, CardBody, EmptyState, Select } from '@solara/ui-shared';
import { useAdminInvoices, useTenants } from '@solara/api-client/react';
import { adaptInvoiceRows, adaptTenants } from '../data/adapters';
import { LoadingBlock, ErrorBanner } from '../components/QueryState';

type StatusFilter = 'all' | 'AP' | 'AB' | 'RE' | 'PENDING';
type DirFilter = 'all' | 'OUTBOUND' | 'INBOUND';

export default function InvoicesPage() {
  const { t } = useTranslation('admin');
  const { locale } = useI18n();
  const aedFmt = new Intl.NumberFormat(locale, { style: 'currency', currency: 'AED' });
  const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' });

  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [tenantFilter, setTenantFilter] = useState<string>(searchParams.get('tenantId') ?? 'all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [directionFilter, setDirectionFilter] = useState<DirFilter>('all');

  // Sync the filter when the user navigates with ?tenantId=...
  useEffect(() => {
    const t = searchParams.get('tenantId');
    if (t && t !== tenantFilter) setTenantFilter(t);
  }, [searchParams, tenantFilter]);

  const invoicesQuery = useAdminInvoices({
    tenantId: tenantFilter === 'all' ? undefined : tenantFilter,
    direction: directionFilter === 'all' ? undefined : directionFilter,
    search: query || undefined,
  });
  const tenantsQuery = useTenants();
  const rows = useMemo(() => adaptInvoiceRows(invoicesQuery.data), [invoicesQuery.data]);
  const tenants = useMemo(() => adaptTenants(tenantsQuery.data), [tenantsQuery.data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (tenantFilter !== 'all' && row.tenantId !== tenantFilter) return false;
      if (statusFilter !== 'all' && row.mlsStatus !== statusFilter) return false;
      if (directionFilter !== 'all' && row.direction !== directionFilter) return false;
      if (!q) return true;
      return (
        row.invoiceNumber.toLowerCase().includes(q) ||
        row.buyerName.toLowerCase().includes(q)
      );
    });
  }, [rows, query, tenantFilter, statusFilter, directionFilter]);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('invoices.title')}</h1>
        <p className="text-fg-muted">{t('invoices.subtitle')}</p>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 sm:flex-initial">
          <Search className="absolute inset-y-0 start-3 my-auto h-4 w-4 text-fg-subtle" />
          <input
            type="search"
            placeholder={t('invoices.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full sm:w-72 h-10 ps-9 pe-3 rounded-md bg-surface border border-border text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
          />
        </div>
        <Select
          value={tenantFilter}
          onChange={(e) => setTenantFilter(e.target.value)}
          options={[
            { value: 'all', label: t('invoices.filterTenant') },
            ...tenants.map((tn) => ({ value: tn.tenantId, label: tn.companyNameEn })),
          ]}
          containerClassName="w-56"
        />
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          options={[
            { value: 'all', label: t('invoices.filterStatus') },
            { value: 'AP', label: 'AP' },
            { value: 'AB', label: 'AB' },
            { value: 'RE', label: 'RE' },
            { value: 'PENDING', label: 'Pending' },
          ]}
          containerClassName="w-36"
        />
        <Select
          value={directionFilter}
          onChange={(e) => setDirectionFilter(e.target.value as DirFilter)}
          options={[
            { value: 'all', label: t('invoices.filterDirection') },
            { value: 'OUTBOUND', label: t('invoices.direction.OUTBOUND') },
            { value: 'INBOUND', label: t('invoices.direction.INBOUND') },
          ]}
          containerClassName="w-36"
        />
      </div>

      <Card>
        <CardBody>
          <div className="text-xs text-fg-muted mb-3">
            {t('invoices.results', { count: filtered.length, total: rows.length })}
          </div>
          {invoicesQuery.isLoading ? (
            <LoadingBlock />
          ) : invoicesQuery.error ? (
            <ErrorBanner error={invoicesQuery.error} />
          ) : filtered.length === 0 ? (
            <EmptyState icon={<Search className="h-6 w-6" />} title={t('invoices.empty')} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-fg-subtle">
                    <th className="text-start font-medium pb-2">{t('invoices.table.invoice')}</th>
                    <th className="text-start font-medium pb-2">{t('invoices.table.tenant')}</th>
                    <th className="text-start font-medium pb-2">{t('invoices.table.buyer')}</th>
                    <th className="text-start font-medium pb-2">{t('invoices.table.issued')}</th>
                    <th className="text-end font-medium pb-2">{t('invoices.table.gross')}</th>
                    <th className="text-end font-medium pb-2">{t('invoices.table.vat')}</th>
                    <th className="text-start font-medium pb-2">{t('invoices.table.direction')}</th>
                    <th className="text-start font-medium pb-2">{t('invoices.table.status')}</th>
                    <th className="pb-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((row) => (
                    <tr key={row.invoiceId} className="group hover:bg-surface-muted/50">
                      <td className="py-2.5">
                        <Link to={`/invoices/${row.invoiceId}`} className="font-medium hover:text-brand-700">
                          {row.invoiceNumber}
                        </Link>
                      </td>
                      <td className="py-2.5 truncate max-w-[20ch]">
                        <div>{tenants.find((tn) => tn.tenantId === row.tenantId)?.companyNameEn ?? row.tenantId.slice(0, 8)}</div>
                      </td>
                      <td className="py-2.5 truncate max-w-[22ch]">{row.buyerName}</td>
                      <td className="py-2.5 num">{dateFmt.format(new Date(row.issueDate))}</td>
                      <td className="py-2.5 text-end num">{aedFmt.format(row.totalGrossAmount)}</td>
                      <td className="py-2.5 text-end num text-fg-muted">{aedFmt.format(row.totalVatAmount)}</td>
                      <td className="py-2.5">
                        <Badge tone={row.direction === 'OUTBOUND' ? 'brand' : 'info'}>
                          {t(`invoices.direction.${row.direction}`)}
                        </Badge>
                      </td>
                      <td className="py-2.5">
                        <Badge tone={statusTone(row.mlsStatus)}>{row.mlsStatus}</Badge>
                      </td>
                      <td className="py-2.5">
                        <Link to={`/invoices/${row.invoiceId}`} className="text-fg-subtle group-hover:text-brand-700">
                          <ChevronRight className="h-4 w-4 rtl-mirror" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function statusTone(status: 'AP' | 'AB' | 'RE' | 'PENDING'): 'success' | 'info' | 'danger' | 'warning' | 'neutral' {
  if (status === 'AP') return 'success';
  if (status === 'AB') return 'info';
  if (status === 'RE') return 'danger';
  return 'warning';
}
